import { FC, useEffect, useState } from 'react';
import { adminService } from '@/services/admin.service';
import { useToast } from '@/contexts/ToastContext';
import type { AdminTag } from '@/types/admin';

const AdminTagsPage: FC = () => {
  const { showToast } = useToast();
  const [tags, setTags] = useState<AdminTag[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('#6B7280');
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#6B7280');

  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    try {
      const data = await adminService.getTags();
      setTags(data);
    } catch { /* ignore */ } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      await adminService.createTag({ name: newName.trim(), color: newColor });
      setNewName('');
      setNewColor('#6B7280');
      await loadTags();
    } catch {
      showToast('Erro ao criar tag', 'error');
    }
  };

  const handleStartEdit = (tag: AdminTag) => {
    setEditingId(tag.id);
    setEditName(tag.name);
    setEditColor(tag.color);
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editName.trim()) return;
    try {
      await adminService.updateTag(editingId, { name: editName.trim(), color: editColor });
      setEditingId(null);
      await loadTags();
    } catch {
      showToast('Erro ao atualizar tag', 'error');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Excluir esta tag?')) return;
    try {
      await adminService.deleteTag(id);
      await loadTags();
    } catch {
      showToast('Erro ao excluir tag', 'error');
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-2xl">
      <h1 className="text-2xl font-bold mb-8">Tags</h1>

      {/* New tag form */}
      <div className="flex gap-3 mb-8">
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          placeholder="Nome da nova tag..."
          className="flex-1 px-4 py-2.5 rounded-xl bg-surface-card border border-theme-border focus:border-accent focus:outline-none"
        />
        <input
          type="color"
          value={newColor}
          onChange={(e) => setNewColor(e.target.value)}
          className="w-12 h-11 rounded-lg cursor-pointer border border-theme-border"
          title="Cor"
        />
        <button
          onClick={handleCreate}
          disabled={!newName.trim()}
          className="bg-accent hover:bg-accent-hover text-accent-contrast px-5 py-2.5 rounded-xl font-medium transition-colors disabled:opacity-50"
        >
          Criar
        </button>
      </div>

      {isLoading ? (
        <p className="text-content-muted">Carregando...</p>
      ) : tags.length === 0 ? (
        <p className="text-content-muted">Nenhuma tag criada.</p>
      ) : (
        <div className="space-y-2">
          {tags.map((tag) => (
            <div
              key={tag.id}
              className="bg-surface-card border border-theme-border rounded-xl px-4 py-3 flex items-center gap-4"
            >
              {editingId === tag.id ? (
                <>
                  <input
                    type="color"
                    value={editColor}
                    onChange={(e) => setEditColor(e.target.value)}
                    className="w-8 h-8 rounded cursor-pointer border border-theme-border"
                  />
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit()}
                    className="flex-1 px-3 py-1.5 rounded-lg bg-surface-primary border border-theme-border focus:border-accent focus:outline-none text-sm"
                    autoFocus
                  />
                  <button onClick={handleSaveEdit} className="text-green-400 hover:text-green-300">
                    <span className="material-symbols-outlined">check</span>
                  </button>
                  <button onClick={() => setEditingId(null)} className="text-content-muted hover:text-content-primary">
                    <span className="material-symbols-outlined">close</span>
                  </button>
                </>
              ) : (
                <>
                  <div className="w-5 h-5 rounded-full shrink-0" style={{ backgroundColor: tag.color }} />
                  <span className="flex-1 font-medium">{tag.name}</span>
                  <span className="text-content-muted text-sm">{tag.slug}</span>
                  <button
                    onClick={() => handleStartEdit(tag)}
                    className="p-1.5 rounded-lg hover:bg-surface-hover text-content-muted hover:text-content-primary"
                  >
                    <span className="material-symbols-outlined text-lg">edit</span>
                  </button>
                  <button
                    onClick={() => handleDelete(tag.id)}
                    className="p-1.5 rounded-lg hover:bg-red-500/10 text-content-muted hover:text-red-400"
                  >
                    <span className="material-symbols-outlined text-lg">delete</span>
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminTagsPage;
